import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { trackEvent } from "@/utils/tagManagerUtils";
import { ArrowRight, Zap, Target, BarChart3, Bot, Clock } from "lucide-react";

const GoogleAdsAutomation = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent("page_view", { path: "/google-ads-automation" });
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads Automation Tool | Upfixe Ads</title>
        <meta
          name="description"
          content="Upfixe is the ultimate Google Ads automation tool. Save hours of manual work, optimize campaigns instantly, and let AI handle the heavy lifting of PPC management."
        />
        <meta
          name="keywords"
          content="google ads automation tool, automated google ads, google ads automation software, automate ppc campaigns"
        />
        <link rel="canonical" href="https://upfixe.com/google-ads-automation" />
      </Helmet>

      <Header />

      <main className="flex-grow pt-24 pb-16">
        {/* Hero Section */}
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center mb-20">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
            The Smartest <span className="text-primary">Google Ads Automation Tool</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Stop wasting hours on manual keyword research, ad copy creation, and bid adjustments. 
            Upfixe automates your entire Google Ads workflow so you can focus on strategy and growth.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="text-lg px-8 py-6 rounded-full">
              <a href="https://app.upfixe.com/signup">
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6 rounded-full">
              <Link to="/mcp">Explore AI Chat Integration</Link>
            </Button>
          </div>
        </section>

        {/* Content Section */}
        <section className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Why you need a Google Ads Automation Tool in 2026</h2>
                <div className="prose prose-lg text-gray-600">
                  <p>
                    Managing Google Ads manually is no longer viable. Between constantly changing search trends, 
                    complex bidding algorithms, and the sheer volume of ad variations needed to succeed, marketers 
                    are spending too much time executing and not enough time strategizing.
                  </p>
                  <p>
                    A dedicated Google Ads automation tool like Upfixe changes the equation. By connecting directly 
                    to your Google Ads account via our secure API, we can generate complete, ready-to-publish 
                    campaigns in minutes based simply on a URL or a natural language prompt.
                  </p>
                  <ul className="space-y-4 mt-8 list-none pl-0">
                    <li className="flex items-start">
                      <Clock className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span><strong>Save 10+ hours per week</strong> previously spent on campaign setup and restructuring.</span>
                    </li>
                    <li className="flex items-start">
                      <Target className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span><strong>Eliminate human error</strong> in keyword match types, negative lists, and targeting settings.</span>
                    </li>
                    <li className="flex items-start">
                      <Zap className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span><strong>Deploy campaigns instantly</strong> as paused drafts, giving you full control over when to go live.</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h3 className="text-2xl font-bold mb-6 text-center">Core Automation Features</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="bg-primary/10 p-3 rounded-lg h-fit">
                      <Bot className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">AI Campaign Generation</h4>
                      <p className="text-gray-600">Give us a URL and we'll automatically generate ad groups, keywords, and responsive search ads.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="bg-primary/10 p-3 rounded-lg h-fit">
                      <BarChart3 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">Automated Reporting</h4>
                      <p className="text-gray-600">Get plain-English summaries of your performance data sent directly to you, no complex dashboards required.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="bg-primary/10 p-3 rounded-lg h-fit">
                      <Zap className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">MCP Integration</h4>
                      <p className="text-gray-600">Connect to Claude, ChatGPT, or other AI agents to manage your ads conversationally.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GoogleAdsAutomation;
